import React, { useState } from 'react';
import toast from 'react-hot-toast';

import ConfirmationModal from '@components/ConfirmationModal';
import IconTrashCan from '@components/Images/Rebranding/IconTrashCan';
import ListItem from '@components/pages/AI/DeepResearchSettings/AiThreadListItem';
import useAiThreadDelete from '@hooks/useAiThreadDelete';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, ImpressionElement, TrackingLocation, click } from '@tracking';
import type { AiThread } from '@utils/backend-api/aiThread';

import { VIEW_THREAD } from '../constants';

interface AiThreadItemProps {
  aiThread: AiThread;
}

const AiThreadItem: React.FC<AiThreadItemProps> = ({ aiThread }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutateAsync: deleteThread, isLoading: isDeletingThread } = useAiThreadDelete();
  const { trackEvent } = useEventTracking({
    contentId: aiThread.id,
    contentType: ContentType.AiThread,
  });
  const { Track } = useEventTracking({
    location: TrackingLocation.AiThreadModal,
  });

  const handleModalOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
    trackEvent(click({ clickTarget: ClickTarget.Open, clickText: 'delete modal' }));
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteThread(aiThread.id);
      toast.success(response.message);
    } catch (error) {
      const errorMessage = error.message || 'Failed to delete thread';
      toast.error(errorMessage);
    } finally {
      setIsModalOpen(false);
    }
  };

  const trackViewClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ViewAiThread, clickText: VIEW_THREAD }));
  };

  const deleteIcon = (
    <div className="relative flex items-center justify-center">
      <IconTrashCan className="h-72 text-white" />
    </div>
  );

  return (
    <>
      <ListItem
        title={aiThread.title}
        date={aiThread.created_at}
        threadId={aiThread.id}
        onViewThreadClick={trackViewClick}
        onActionClick={handleModalOpen}
        actionAriaLabel="Delete AI thread"
      >
        <Track>
          <ConfirmationModal
            cancelClickTarget={ClickTarget.Cancel}
            cancelText="Cancel"
            confirmClickTarget={ClickTarget.Delete}
            confirmingText="Deleting…"
            confirmText="Delete"
            description="Are you sure you want to delete this research thread? This action cannot be undone."
            impressionElement={ImpressionElement.AIThreadDeletionModal}
            isLoading={isDeletingThread}
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onConfirm={handleDeleteConfirm}
            rightIcon={deleteIcon}
            title="Delete thread"
          />
        </Track>
      </ListItem>
    </>
  );
};

export default AiThreadItem;
